/**
 * Driver-facing on-call availability toggle.
 * Shows the current on/off state, the €10 bonus window (02:00–05:00
 * Europe/Zagreb), and last night's award if any.
 *
 * Only displays for drivers whose `fee_model` is eligible (fleet cars).
 * For ineligible drivers the toggle is still shown (they may become
 * fleet later) but the bonus copy is greyed out.
 */
import { useEffect, useState } from "react";
import { Zap, ZapOff, Clock, Award, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

export default function AvailabilityToggle() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState({ rows: [], sum_eur: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, h] = await Promise.all([
        driverApi.get("/driver/availability"),
        driverApi.get("/driver/on-call-bonus/history"),
      ]);
      setState(a.data);
      setHistory(h.data);
    } catch {
      /* silent */
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!state) return;
    setBusy(true);
    const next = !state.available;
    try {
      const { data } = await driverApi.post(
        "/driver/availability", { available: next },
      );
      setState((s) => ({ ...s, ...data }));
      toast.success(next ? "Sada ste dežurni." : "Dežurstvo isključeno.");
    } catch {
      toast.error("Ažuriranje dostupnosti nije uspjelo.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const on = state.available;
  const eligible = state.eligible_for_bonus;

  return (
    <div data-testid="availability-card"
         className={`mb-4 rounded-2xl border p-4 transition-colors ${
           on
             ? "border-emerald-500/40 bg-emerald-500/[0.05]"
             : "border-white/10 bg-[#0d0d0d]"
         }`}>
      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          disabled={busy}
          data-testid="availability-toggle-btn"
          aria-pressed={on}
          className={`w-14 h-14 rounded-full flex items-center justify-center border transition-colors disabled:opacity-50 ${
            on
              ? "bg-emerald-500 border-emerald-400 text-black"
              : "bg-zinc-900 border-white/15 text-zinc-400 hover:text-white hover:border-white/30"
          }`}
        >
          {on ? <Zap className="w-6 h-6" /> : <ZapOff className="w-6 h-6" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium"
             data-testid="availability-state-label">
            {on ? "Dežurni · primate vožnje" : "Niste dežurni"}
          </p>
          <p className="text-[11px] text-zinc-400 flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" />
            Bonus termin <span className="tabular-nums">{state.bonus_window_local}</span>
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
      </div>

      {/* Bonus copy */}
      <div className={`mt-3 rounded-lg border p-3 ${
        eligible ? "border-[#C5A059]/30 bg-[#C5A059]/[0.05]" : "border-white/10 bg-black/40 opacity-60"
      }`}>
        <div className="flex items-start gap-2">
          <Award className={`w-4 h-4 mt-0.5 shrink-0 ${
            eligible ? "text-[#C5A059]" : "text-zinc-500"
          }`} />
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-medium ${
              eligible ? "text-white" : "text-zinc-400"
            }`}>
              €{state.bonus_amount_eur.toFixed(2)} bonus po noći dežurstva
            </p>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {eligible
                ? "Ostanite dežurni cijeli termin bez ijedne vožnje → isplata u ponedjeljak."
                : "Bonus ostvaruju samo vozači flote."}
            </p>
          </div>
          {history.sum_eur > 0 && (
            <div className="text-right shrink-0" data-testid="availability-week-total">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Ovaj tjedan</p>
              <p className="font-display text-lg text-[#C5A059] tabular-nums">
                €{Number(history.sum_eur).toFixed(2)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
